import type { AdvancedFilterQuery, Cohort } from '@packages/types';
import type { FormInstance } from 'antd';

// ============================================================================
// Pagination Types
// ============================================================================

export interface IPagination {
    current?: number;
    pageSize?: number;
    total?: number;
}

/**
 * Pagination state type with required current and pageSize for internal component state
 */
export interface IPaginationState {
    current: number;
    pageSize: number;
    total?: number;
}

// ============================================================================
// Step Types
// ============================================================================

export const STEP_IDS = {
    CRITERIA_SELECTION: 'criteria_selection',
    PATIENT_MANAGEMENT: 'patient_management',
    COHORT_DETAILS: 'cohort_details',
    REVIEW: 'review',
} as const;

export type StepId = (typeof STEP_IDS)[keyof typeof STEP_IDS];

export interface StepConfig {
    id: StepId;
    title: string;
    icon: React.ReactNode;
}

// ============================================================================
// Patient Types
// ============================================================================

export interface IPatientRecordMetadata {
    care_site_name: string | null;
    patient_gender: string;
    place_of_service_source_value: string | null;
    note_ingestion_datetime: number;
    visit_type: string;
    source: string;
    document_id: string;
    care_site_source_value: string | null;
    patient_month_of_birth: number;
    patient_birth_datetime: string;
    patient_race: string;
    note_datetime: string;
    patient_year_of_birth: number;
    patient_id: string;
    visit_id: string;
    note_date: string;
    patient_day_of_birth: number;
    patient_ethnicity: string;
    visit_timestamp: string;
    visit_end_timestamp: string;
    visit_start_timestamp: string;
    document_type: string;
}

export interface IPatientRecord {
    id: string;
    noteId?: string;
    name: string;
    type: 'note';
    date: string;
    patientId: string;
    dataSource: string;
    documentType: string;
    fileName?: string;
    events?: any[];
    metadata?: IPatientRecordMetadata;
    matched?: boolean; // Flag indicating if this document matched document-level filters
    matchedConditions?: string[]; // Which condition fields this document matched (e.g., ['documentType', 'sentenceContext'])
}

export interface ICareSite {
    name?: string;
    placeOfService?: string;
}

export interface IPatient {
    id: string;
    name: string;
    age: number;
    gender: string;
    race?: string;
    ethnicity?: string;
    birthDate?: string;
    diagnosis_list?: Array<{ name: string; icd_code: string | null; start_date: string | null }>;
    diagnosis: string[];
    documentCount?: number;
    records: IPatientRecord[];
    careSite?: ICareSite;
    noteCount: number;
    omopPersonId?: string;
    sourcePatientId?: string;
}

// ============================================================================
// Form Values Types
// ============================================================================

export interface CohortFormValues {
    name: string;
    description: string;
    category_mode: 'new' | 'existing';
    new_category_name: string;
    category_id?: string;
    patient_ids: string[];
    linked_cohort_ids: string[];
    patientAdvancedFilter?: AdvancedFilterQuery & {
        excludedPatientIds?: string[];
    };
    /** When true and diagnosis filter exists, include patients from ES NLP matches (person_source_value) */
    includeElasticsearchForFilters?: boolean;
    excludedPatientsData?: IPatient[];
    chat_filter?: string;
    sql_query?: string;
    randomEnabled?: boolean;
    randomCount?: number;
    randomSeed?: string;
    cohort_mode?: 'filter' | 'chat' | 'clinical_trial' | 'linked_cohort';
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface PatientExcludeProps {
    form: FormInstance<CohortFormValues>;
    setCurrentStepId: (stepId: StepId) => void;
    goBack: () => void;
    excludedPatients: IPatient[];
    setExcludedPatients: (patients: IPatient[]) => void;
    setFilteredTotalCount?: (count: number) => void;
    messageId?: string;
    total: number;
}

export interface PatientSelectionProps {
    form: FormInstance<CohortFormValues>;
    isLazyLoadingPatienList: boolean;
    setPagination: (setter: IPagination | ((prev: IPagination) => IPagination)) => void;
    showFilter: boolean;
    setShowFilter: (e: boolean | ((prev: boolean) => boolean)) => void;
    pagination: IPagination;
    loadingPatients: boolean;
    patientsData: IPatient[];
    total: number;
    totalUnfiltered: number;
    setFilter: (filter: string | undefined) => void;
    filter: string | undefined;
    isFormLoading: boolean;
    onSearch: (search: string) => void;
    onOrderChange: (orderBy: string, orderDirection: 'asc' | 'desc') => void;
    goNext: () => void;
    goBack?: () => void;
    messageId?: string | null;
    clinicalTrialDescription?: string | null;
}

export interface PatientListSelectionProps {
    data: IPatient[];
    total?: number;
    totalUnfiltered?: number;
    loading?: boolean;
    setFilter?: (filter: string | undefined) => void;
    filter?: string;
    isLazyLoadingPatienList?: boolean;
    form?: FormInstance<CohortFormValues>;
    setPagination?: (setter: IPagination | ((prev: IPagination) => IPagination)) => void;
    pagination?: IPagination;
    showFilter?: boolean;
    isFormLoading?: boolean;
    setShowFilter?: (e: boolean | ((prev: boolean) => boolean)) => void;
    onSearch?: (search: string) => void;
    onOrderChange?: (orderBy: string, orderDirection: 'asc' | 'desc') => void;
    messageId?: string | null;
    clinicalTrialDescription?: string | null;
}

export interface CohortDetailsProps {
    form: FormInstance<CohortFormValues>;
    errors: Record<string, string>;
    clearError: (field: string) => void;
    handleSmartGenerate: () => Promise<void>;
    isGenerating: boolean;
    filteredTotalCount: number;
    excludedPatients: IPatient[];
    selectedLinkedCohorts: Cohort[];
    setSelectedLinkedCohorts: (cohorts: Cohort[]) => void;
    goBack: () => void;
    creatingCohort: boolean;
    setCurrentStepId: (stepId: StepId) => void;
}

export interface CohortReviewProps {
    form: FormInstance<CohortFormValues>;
    creatingCohort: boolean;
    patientsData: IPatient[];
    loadingPatients: boolean;
    total: number;
    selectedLinkedCohorts: Cohort[];
    patientSearchTerm: string;
    patientOrderBy: string;
    patientOrderDirection: 'asc' | 'desc' | '';
    messageId?: string;
    goBack: () => void;
}
